// src/pages/items/ItemList.tsx
import { Column, DataTable } from '@/components/common/DataTable';
import DeleteConfirmDialog from '@/components/common/DeleteConfirmDialog';
import SelectDropdown from '@/components/common/SelectDropdown';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import useDebounce from '@/hooks/use-debounce';
import { DEFAULT_PAGINATION, getTaxPreferenceLabel, taxPreferences } from '@/lib/constants/common';
import { getItemTypeLabel, itemTypes } from '@/lib/constants/items';
import { itemsAPI } from '@/lib/utils/api';
import { Pagiantion } from '@/types/common.type';
import { Item } from '@/types/items.types';
import { Edit, Files, Plus, Trash2 } from 'lucide-react';
import { ChangeEvent, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

const ItemList = () => {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(search, 300);

  const [itemTypeFilter, setItemTypeFilter] = useState<string>('all');
  const [taxPreferenceFilter, setTaxPreferenceFilter] = useState<string>('all');

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = localStorage.getItem('items_rowsPerPage');
    return saved ? Number(saved) : 10;
  });
  const [totalItems, setTotalItems] = useState<number>(0);

  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
    localStorage.setItem('items_rowsPerPage', String(rows));
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Item | null>(null);
  const [deleting, setDeleting] = useState(false);
  const location = useLocation() as { state?: { newItem?: Item } };
  const fetchItems = async (newItem?: Item): Promise<void> => {
    try {
      setLoading(true);

      const params: any = {};
      if (debouncedSearch) params.search = debouncedSearch;
      if (itemTypeFilter && itemTypeFilter !== 'all') params.itemType = itemTypeFilter;
      if (taxPreferenceFilter && taxPreferenceFilter !== 'all')
        params.taxPreference = taxPreferenceFilter;
      params.page = currentPage;
      params.limit = rowsPerPage;

      const response = await itemsAPI.getAll(params);

      const {
        total,
        limit = rowsPerPage,
        page,
        pages,
      }: Pagiantion = response.data?.pagination ?? DEFAULT_PAGINATION;

      const flatItems: Item[] = (response.data.items || response.data).map((item: Item) => ({
        ...item,
        currency:
          item.currency && ['INR', 'USD', 'EUR'].includes(item.currency) ? item.currency : 'INR',
      }));

      // setItems(flatItems);
      if (newItem) {
        const normalizedNewItem: Item = {
          ...newItem,
          currency:
            newItem.currency && ['INR', 'USD', 'EUR'].includes(newItem.currency)
              ? newItem.currency
              : 'INR',
        };

        const updatedItems = [
          normalizedNewItem,
          ...flatItems.filter((i) => i._id !== normalizedNewItem._id),
        ];
        setItems(updatedItems);
      } else {
        setItems(flatItems);
      }
      setTotalItems(total);
    } catch {
      toast.error('Failed to fetch items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, rowsPerPage, itemTypeFilter, taxPreferenceFilter]);

  // useEffect(() => {
  //   fetchItems();
  // }, [currentPage, debouncedSearch, rowsPerPage, itemTypeFilter, taxPreferenceFilter]);
  useEffect(() => {
    const newItem = location.state?.newItem;
    fetchItems(newItem);
  }, [currentPage, debouncedSearch, rowsPerPage, itemTypeFilter, taxPreferenceFilter]);

  const handleDeleteClick = (item: Item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      setDeleting(true);
      await itemsAPI.delete(itemToDelete._id);
      toast.success('Item deleted successfully');
      fetchItems();
    } catch {
      toast.error('Failed to delete item');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleDuplicate = async (item: Item): Promise<void> => {
    try {
      const duplicatedData = {
        ...item,
        _id: undefined,
        itemName: `${item.itemName} (Copy)`,
      };

      await itemsAPI.create(duplicatedData);
      toast.success('Item duplicated successfully');
      fetchItems();
    } catch {
      toast.error('Failed to duplicate item');
    }
  };

  const columns: Column<Item>[] = [
    {
      key: 'item',
      header: 'Item',
      cell: (item) => (
        <>
          <div className="font-medium whitespace-nowrap w-fit min-w-fit">{item.itemName}</div>
          {item.description && (
            <div className="text-sm text-muted-foreground whitespace-break-spaces max-w-md">
              {item.description}
            </div>
          )}
        </>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      cell: (item) => (
        <Badge
          variant="secondary"
          className={`capitalize ${
            item.itemType === 'service'
              ? 'bg-blue-100 text-blue-800'
              : 'bg-green-100 text-green-800'
          }`}
        >
          {item.itemType ? getItemTypeLabel(item.itemType) : '-'}
        </Badge>
      ),
    },
    {
      key: 'code',
      header: 'SAC/HSN Code',
      cell: (item) => (
        <div className="font-mono text-sm">
          {item.itemType === 'service' ? item.sacCode || '-' : item.hsnCode || '-'}
        </div>
      ),
    },
    {
      key: 'rate',
      header: 'Rate',
      cell: (item) => <div className="text-sm">{item.sellingPrice ?? item.rate ?? '-'}</div>,
    },
    {
      key: 'tax',
      header: 'Tax Preference',
      cell: (item) => (
        <div className="text-sm">
          {item?.taxPreference ? getTaxPreferenceLabel(item.taxPreference) : '-'}
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'text-right sticky right-0 bg-card',
      cell: (item) => (
        <div className="flex justify-end gap-2">
          <Button
            asChild
            variant="ghost"
            size="icon"
            className="text-blue-600 hover:bg-blue-50 hover:text-blue-600"
          >
            <Link to={`/items/${item._id}/edit`}>
              <Edit className="!h-[18px] !w-[18px]" />
            </Link>
          </Button>
          <Button variant="ghost" size="icon" onClick={() => handleDuplicate(item)}>
            <Files className="!h-[18px] !w-[18px]" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-red-600 hover:bg-red-50 hover:text-red-600"
            onClick={() => handleDeleteClick(item)}
          >
            <Trash2 className="!h-[18px] !w-[18px]" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <p className="text-muted-foreground">Manage your products and services</p>
        </div>
        <Button asChild>
          <Link to="/items/new">
            <Plus className="mr-2 h-4 w-4" />
            Add Item
          </Link>
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
            <div className="space-y-2">
              <Label>Search</Label>
              <Input
                type="search"
                placeholder="Search by item name, description, SAC or HSN code"
                value={search}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Item Type</Label>
              <SelectDropdown
                value={itemTypeFilter}
                options={[{ value: 'all', label: 'All Item Types' }, ...itemTypes]}
                placeholder="All Item Types"
                onChange={(val) => setItemTypeFilter(val)}
              />
            </div>

            <div className="space-y-2">
              <Label>Tax Preference</Label>
              <SelectDropdown
                value={taxPreferenceFilter}
                options={[{ value: 'all', label: 'All Tax Preferences' }, ...taxPreferences]}
                placeholder="All Tax Preferences"
                onChange={(val) => setTaxPreferenceFilter(val)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4 rounded-lg border p-4">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-[250px]" />
                    <Skeleton className="h-4 w-[200px]" />
                  </div>
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center">
              <h3 className="mb-2 text-lg font-semibold">No items found</h3>
              <p className="mb-4 text-muted-foreground">
                {search ? 'Try a different search term' : 'Get started by adding your first item'}
              </p>
              <Button asChild>
                <Link to="/items/new">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Item
                </Link>
              </Button>
            </div>
          ) : (
            <DataTable
              data={items}
              columns={columns}
              currentPage={currentPage}
              rowsPerPage={rowsPerPage}
              total={totalItems}
              onPageChange={setCurrentPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              emptyMessage="No items found"
            />
          )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        entityName={itemToDelete?.itemName}
        loading={deleting}
        onDelete={handleDeleteConfirm}
      />
    </div>
  );
};

export default ItemList;
